import { DialogTrigger, Dialog, Modal } from 'react-aria-components';
import { useForm, SubmitHandler } from 'react-hook-form';

import { yupResolver } from '@hookform/resolvers/yup';
import { useAtom } from 'jotai';
import * as yup from 'yup';

import { createSchool } from '@/api/createSchool';
import { updateSchool } from '@/api/updateSchool';
import Button from '@/components/Button/Button';
import InputText from '@/components/InputText/InputText';
import MultiSelectField from '@/components/SelectField/MultiSelectField';
import SelectField from '@/components/SelectField/SelectField';
import { PREFECTURE_OPTIONS } from '@/const/prefecture';
import { NotificationAtom, UserAtom } from '@/jotai/jotai';
import { Office } from '@/types/office';
import { School, CreateSchoolRequest } from '@/types/school';

type Props = {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  afterSubmit: () => void;
  school?: School;
  offices: Office[];
};

const schoolTypes = ['小学校', '中学校', '高校'];

export const CreateOrEditSchoolDialog = ({
  isOpen,
  setIsOpen,
  afterSubmit,
  school,
  offices,
}: Props) => {
  const isEdit = school !== undefined;

  const [_, setNotification] = useAtom(NotificationAtom);
  const [loginUser] = useAtom(UserAtom);

  const schema: yup.ObjectSchema<CreateSchoolRequest> = yup
    .object({
      name: yup.string().required('学校名は必須です'),
      type: yup.string().required('種別は必須です'),
      postalCode: yup.string().required('郵便番号は必須です'),
      prefectureId: yup.number().required('都道府県は必須です'),
      municipality: yup.string().required('市区町村は必須です'),
      address1: yup.string().required('住所1は必須です'),
      address2: yup.string(), // オプショナル
      phoneNumber: yup.string().required('電話番号は必須です'),
      office_ids: yup.array().of(yup.number().defined()).required('事業所選択は必須です'),
    })
    .required();

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<CreateSchoolRequest>({
    resolver: yupResolver(schema),
    defaultValues: {
      name: school ? school.name : '',
      type: school ? school.type : '',
      postalCode: school ? school.postalCode : '',
      prefectureId: school ? school.prefectureId : 0,
      municipality: school ? school.municipality : '',
      address1: school ? school.address1 : '',
      address2: school ? school.address2 : '',
      phoneNumber: school ? school.phoneNumber : '',
      office_ids: school ? school.office_ids : [],
    },
  });

  const onSubmit: SubmitHandler<CreateSchoolRequest> = async (data) => {
    if (!isEdit) {
      try {
        const result = await createSchool({
          name: data.name,
          type: data.type,
          postalCode: data.postalCode,
          prefectureId: data.prefectureId,
          municipality: data.municipality,
          address1: data.address1,
          address2: data.address2 || '',
          phoneNumber: data.phoneNumber,
          office_ids: data.office_ids,
        });
        console.log(result);
        if (result.status === 201) {
          console.log(result.result);
          setNotification({
            headingLevel: 'h2',
            type: 'success',
            bannerStyle: 'standard',
            title: '学校マスタを登録しました',
            message: '学校マスタを登録しました',
          });
        } else {
          console.log('error');
          setNotification({
            headingLevel: 'h2',
            type: 'error',
            bannerStyle: 'standard',
            title: '学校マスタの登録に失敗しました',
            message: '学校マスタの登録に失敗しました',
          });
        }
      } catch (error) {
        console.log('error');
        setNotification({
          headingLevel: 'h2',
          type: 'error',
          bannerStyle: 'standard',
          title: '学校マスタの登録に失敗しました',
          message: '学校マスタの登録に失敗しました',
        });
      }
    } else {
      try {
        const result = await updateSchool(school!.id, {
          id: school!.id,
          name: data.name,
          type: data.type,
          postalCode: data.postalCode,
          prefectureId: data.prefectureId,
          municipality: data.municipality,
          address1: data.address1,
          address2: data.address2 || '',
          phoneNumber: data.phoneNumber,
          office_ids: data.office_ids,
        });
        if (result.status === 200) {
          setNotification({
            headingLevel: 'h2',
            type: 'success',
            bannerStyle: 'standard',
            title: '学校マスタを更新しました',
            message: '学校マスタを更新しました',
          });
        } else {
          setNotification({
            headingLevel: 'h2',
            type: 'error',
            bannerStyle: 'standard',
            title: '学校マスタの更新に失敗しました',
            message: '学校マスタの更新に失敗しました',
          });
        }
      } catch (error) {
        setNotification({
          headingLevel: 'h2',
          type: 'error',
          bannerStyle: 'standard',
          title: '学校マスタの更新に失敗しました',
          message: '学校マスタの更新に失敗しました',
        });
      }
    }

    setIsOpen(false);
    afterSubmit();
  };

  return (
    <DialogTrigger
      isOpen={isOpen}
      onOpenChange={() => setIsOpen(false)}
    >
      <Modal className="fixed inset-0 z-50 flex h-full w-full items-center justify-center bg-black/50">
        <Dialog className="w-[900px] rounded-lg border border-gray-400 bg-white p-6 shadow-lg outline-none">
          <div className="flex flex-row justify-between">
            <h1 className="mb-5 text-2xl font-700">
              {isEdit ? '学校マスタ編集' : '学校マスタ登録'}
            </h1>
          </div>

          <form
            onSubmit={handleSubmit(onSubmit)}
            className="w-full p-10"
          >
            <div className="flex justify-between space-x-2 p-4">
              <InputText
                label="学校名"
                type="text"
                required={true}
                {...register('name')}
              />
              <SelectField
                name="type"
                label="学校種類"
                control={control}
                items={schoolTypes.map((type) => ({
                  label: type,
                  value: type,
                }))}
                required={true}
              />
            </div>
            <div className="flex justify-between space-x-2 p-4">
              <InputText
                label="郵便番号"
                type="text"
                required={true}
                {...register('postalCode')}
              />
              <SelectField
                name="prefectureId"
                label="都道府県"
                control={control}
                items={PREFECTURE_OPTIONS.map((prefecture) => ({
                  label: prefecture.name,
                  value: prefecture.id,
                }))}
                required={true}
              />
            </div>
            <div className="flex justify-between space-x-2 p-4">
              <InputText
                label="市区町村"
                type="text"
                required={true}
                {...register('municipality')}
              />
              <InputText
                label="住所1"
                type="text"
                required={true}
                {...register('address1')}
              />
            </div>
            <div className="flex justify-between space-x-2 p-4">
              <InputText
                label="住所2"
                type="text"
                {...register('address2')}
              />
              <InputText
                label="電話番号"
                type="text"
                required={true}
                {...register('phoneNumber')}
              />
            </div>
            {loginUser?.is_super_admin && (
              <div className="flex justify-between space-x-2 p-4">
                <MultiSelectField
                  name="office_ids"
                  label="事業所"
                  placeholder="事業所を選択"
                  options={offices
                    .filter((office) => loginUser && loginUser.office_ids.includes(office.id))
                    .map((office) => ({
                      label: office.name,
                      value: office.id,
                    }))}
                  defaultValue={offices
                    .filter((office) => school && school.office_ids.includes(office.id))
                    .map((office) => ({
                      label: office.name,
                      value: office.id,
                    }))}
                  control={control}
                  required
                />
              </div>
            )}
            <div className="mt-10 flex flex-row items-center justify-center space-x-2">
              <Button
                type="submit"
                size="lg"
                variant="outline"
                onClick={() => setIsOpen(false)}
              >
                戻る
              </Button>
              <Button
                type="submit"
                size="lg"
                variant={Object.keys(errors).length > 0 ? 'disabled' : 'solid-fill'}
                disabled={Object.keys(errors).length > 0}
              >
                {isEdit ? '保存' : '登録'}
              </Button>
            </div>
          </form>
        </Dialog>
      </Modal>
    </DialogTrigger>
  );
};
